<script lang="ts">
  import { Icon, type IconNames } from '@shared/ui/icon';
  import { Portal } from '@shared/ui/portal';
  import { cn, is } from '@shared/utils';

  import type { TooltipPositionX } from '../types';

  export let positionX: TooltipPositionX = 'center';
  export let iconName: IconNames | null = 'info';
  export let iconWidth: number | undefined = 16;
  export let iconHeight: number | undefined = 16;
  export let iconClasses: string | undefined = '';
  export let isTooltipVisible = false;
  export let isFixed = false;

  export let tooltipClasses: string = '';
  export let contentWrapperClasses: string = '';

  let tooltipEl: HTMLDivElement;
  let portalInnerEl: HTMLDivElement;

  const setPositionX = (val: TooltipPositionX) => {
    positionX = val;
  };

  const styles = {
    x: {
      left: 'before:right-6 -translate-x-full ml-[30px]',
      right: 'before:left-6  -ml-6',
      center: 'before:left-1/2 -translate-x-1/2',
    },
  };

  const showTooltip = (event: MouseEvent): void => {
    const parentEl = event.target as HTMLElement;
    const parentPos = parentEl.getBoundingClientRect();
    const extraSpace = 10; // add some extra space for better view
    const centerXPos = parentPos.x + parentPos.width / 2;

    const posX = isFixed ? centerXPos : centerXPos + window.scrollX;
    const posY = isFixed
      ? parentPos.y - (parentPos.height * 2 + extraSpace)
      : parentPos.y + parentPos.height / 2 + window.scrollY;

    if (portalInnerEl && parentPos) {
      portalInnerEl.style.left = `${posX}px`;
      portalInnerEl.style.top = `${posY}px`;
    }

    const tooltipWidth = tooltipEl.clientWidth + extraSpace;
    const tooltipMargin = 24; // ml-6 or -ml-6 equals 24px
    const pageWidth: number = document.body.clientWidth;

    //checking all available positions
    const fitRight: boolean = posX + tooltipWidth - tooltipMargin <= pageWidth;
    const fitLeft: boolean = posX - tooltipWidth + tooltipMargin >= 0;
    const fitCenter: boolean = posX + tooltipWidth / 2 <= pageWidth && posX - tooltipWidth / 2 >= 0;

    //checking if the tooltip fits props:position and picking the closest available
    switch (positionX) {
      case 'center':
        if (!fitCenter) setPositionX(fitLeft ? 'left' : 'right');
        break;
      case 'left':
        if (!fitLeft) setPositionX(fitCenter ? 'center' : 'right');
        break;
      case 'right': {
        if (!fitRight) setPositionX(fitCenter ? 'center' : 'left');
      }
    }

    isTooltipVisible = true;
  };

  const hideTooltip = (): void => {
    isTooltipVisible = false;
  };
</script>

<Portal>
  <div
    bind:this="{portalInnerEl}"
    class="{is(!isTooltipVisible, 'pointer-events-none opacity-0', 'opacity-100')} absolute z-50 transition-opacity"
  >
    <div
      bind:this="{tooltipEl}"
      role="tooltip"
      class="text-default-200 before:border-l-6 before:border-r-6
      bg-default-900 before:border-t-default-900 font-nue
      before:border-t-6
      dark:bg-dark-800
      dark:text-dark-200
      dark:before:border-t-dark-800
      pointer-events-none
      z-30 mb-4 whitespace-normal
      rounded px-3 py-2 text-xs
      normal-case backdrop-blur-md
      before:-bottom-1.5 before:-ml-1.5
      before:h-1.5 before:w-3
      before:border-l-transparent
      before:border-r-transparent
      group-hover/tooltip:pointer-events-auto
      group-hover/tooltip:opacity-100
      group-focus/tooltip:pointer-events-auto
      group-focus/tooltip:opacity-100
      {styles.x[positionX]}
      {tooltipClasses}
      {cn('absolute before:absolute', { 'fixed before:fixed': isFixed, 'bottom-full left-1/2': !isFixed })}
    "
    >
      <slot />
    </div>
  </div>
</Portal>

<div
  on:mouseenter="{showTooltip}"
  on:mouseleave="{hideTooltip}"
  class="{cn('group/tooltip relative inline-flex shrink-0 cursor-pointer', contentWrapperClasses)}"
  tabindex="-1"
  role="button"
>
  <slot name="content" />

  {#if iconName}
    <Icon name="{iconName}" width="{iconWidth}" height="{iconHeight}" classes="{iconClasses}" />
  {/if}
</div>
